import { ErrorBoundary } from '@sentry/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { ScopeProvider } from 'jotai-scope';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { filtersAtom } from '@/atoms/filters/filter';
import { competitionIdsToFilterByAtom } from '@/atoms/filters/highLevel/competitions';
import { seasonIdsToFilterByAtom } from '@/atoms/filters/highLevel/seasons';
import { playerIdAtom } from '@/atoms/player/player';
import { sectionAtom } from '@/atoms/section';
import { filterAtoms, radarAtoms } from '@/consts/scope';
import { PLAYER_ID } from '@/consts/searchKeys';
import { useSetPlayerEventDataDebouncedFilterParams } from '@/hooks/apiFilterParams/useSetPlayerEventDataDebouncedFilterParams';
import { useDeepEffect } from '@/hooks/useDeepEffect';
import { ConfigFilterAtomValues } from '@/types/userConfigs';
import { numArraysEqual } from '@/utils/array';
import { GeneralErrorBoundaryFallback } from '../../errorBoundary/ErrorBoundaryFallback';
import { PlayerPreviewBody } from './PlayerPreviewBody';
import { PlayerPreviewHeader } from './PlayerPreviewHeader';

// Stabilise the array reference so the component only re-renders when the IDs
// actually change. Without this, Jotai's unwrap allocates a new [] each time
// the async options atom settles, triggering a spurious re-render.
const stableCompetitionIdsAtom = selectAtom(competitionIdsToFilterByAtom, ids => ids, numArraysEqual);
const stableSeasonIdsAtom = selectAtom(seasonIdsToFilterByAtom, ids => ids, numArraysEqual);

type SetPlayerPreviewDataProps = {
  pageFilters: ConfigFilterAtomValues;
  resolvedCompetitionIds: number[];
  resolvedSeasonIds: number[];
};

const SetPlayerPreviewData = ({
  pageFilters,
  resolvedCompetitionIds,
  resolvedSeasonIds,
}: SetPlayerPreviewDataProps) => {
  const [searchParams] = useSearchParams();
  const playerId = searchParams.get(PLAYER_ID);
  const setPlayerId = useSetAtom(playerIdAtom);
  const setSection = useSetAtom(sectionAtom);
  const setFilters = useSetAtom(filtersAtom);

  useEffect(() => {
    if (playerId) {
      setPlayerId(Number(playerId));
    }
  }, [playerId]);

  useEffect(() => {
    setSection('player');
  }, []);

  // Hydrate the preview-scoped filtersAtom with the page's resolved filter values.
  // We use the resolved IDs (not the raw filtersAtom values) so that null/"use default"
  // values on the page don't resolve to the player's own section defaults, which differ
  // from the page's section defaults (e.g. a player's most recent competition vs the
  // page's default competition).
  useDeepEffect(() => {
    setFilters({
      ...pageFilters,
      competitionIds: resolvedCompetitionIds,
      seasonIds: resolvedSeasonIds,
    });
  }, [pageFilters, resolvedCompetitionIds, resolvedSeasonIds]);

  // The preview applies page filters immediately — no debounce needed.
  useSetPlayerEventDataDebouncedFilterParams(0);

  return null;
};

export const PlayerPreview = ({ dashboardHref }: { dashboardHref?: string }) => {
  const [searchParams] = useSearchParams();
  const playerId = searchParams.get(PLAYER_ID);
  // Read the page's resolved filter values before entering the preview's scope.
  // We read the resolved IDs rather than the raw filtersAtom so that null/"use default"
  // values are expanded to their actual IDs in the page's section context.
  const pageFilters = useAtomValue(filtersAtom);
  const resolvedCompetitionIds = useAtomValue(stableCompetitionIdsAtom);
  const resolvedSeasonIds = useAtomValue(stableSeasonIdsAtom);

  if (!playerId) return null;

  return (
    // filterAtoms must be scoped even though we want the preview to reflect the page's filters.
    // sectionAtom is set to 'player' inside the scope (required for radar), which changes how
    // null/"use default" filter values resolve — player defaults differ from page defaults (e.g.
    // a player's most recent competition vs the page's team competition). We work around this by
    // writing the page's already-resolved IDs into the scoped filtersAtom via SetPlayerPreviewData.
    // filterAtoms must be scoped so those writes don't mutate the page's filter state.
    <ScopeProvider atoms={[...filterAtoms, ...radarAtoms, sectionAtom, playerIdAtom]}>
      <SetPlayerPreviewData
        pageFilters={pageFilters}
        resolvedCompetitionIds={resolvedCompetitionIds}
        resolvedSeasonIds={resolvedSeasonIds}
      />
      <ErrorBoundary fallback={<GeneralErrorBoundaryFallback />}>
        <div className="flex flex-col h-full">
          <PlayerPreviewHeader dashboardHref={dashboardHref} />
          <PlayerPreviewBody />
        </div>
      </ErrorBoundary>
    </ScopeProvider>
  );
};
